import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarClock, CalendarPlus, X, Check, Stethoscope, Phone } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

// Dates are local-calendar "YYYY-MM-DD" strings (en-CA), matching how visits and appointments are stored.
const ymd = (d) => d.toLocaleDateString("en-CA");
const parse = (s) => new Date(`${s}T00:00:00`);
const addDays = (s, n) => { const d = parse(s); d.setDate(d.getDate() + n); return ymd(d); };
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DEFAULT_TIME = "10:00";

const timeLabel = (t) => {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

// Monday-first grid covering the whole month, padded to full weeks.
function monthGrid(month) {
  const first = parse(`${month}-01`);
  const start = addDays(`${month}-01`, -((first.getDay() + 6) % 7));
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  const end = addDays(ymd(last), 6 - ((last.getDay() + 6) % 7));
  const days = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
  return days;
}

export default function Calendar() {
  const { data, save } = useApp();
  const today = ymd(new Date());
  const [params, setParams] = useSearchParams();
  const selected = /^\d{4}-\d{2}-\d{2}$/.test(params.get("date") || "") ? params.get("date") : today;
  const [month, setMonth] = useState(selected.slice(0, 7));

  const select = (d) => { setParams({ date: d }, { replace: true }); if (d.slice(0, 7) !== month) setMonth(d.slice(0, 7)); };
  const shiftMonth = (n) => {
    const d = parse(`${month}-01`);
    d.setMonth(d.getMonth() + n);
    setMonth(ymd(d).slice(0, 7));
  };

  const appts = useMemo(() => {
    const by = {};
    for (const p of data.patients) {
      const d = (p.next_appointment || "").slice(0, 10);
      if (d) (by[d] ||= []).push(p);
    }
    for (const list of Object.values(by)) list.sort((a, b) => (a.next_appointment_time || "").localeCompare(b.next_appointment_time || ""));
    return by;
  }, [data.patients]);

  const visits = useMemo(() => {
    const by = {};
    for (const v of data.visits) {
      const d = (v.date || "").slice(0, 10);
      if (d) (by[d] ||= []).push(v);
    }
    return by;
  }, [data.visits]);

  const days = useMemo(() => monthGrid(month), [month]);
  const monthTitle = parse(`${month}-01`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const upcoming = useMemo(
    () => Object.entries(appts).filter(([d]) => d >= today).reduce((a, [, l]) => a + l.length, 0),
    [appts, today]
  );

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="dm-page-title">
            Clinic <span className="font-extrabold">calendar</span>
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            <span className="font-mono font-semibold text-slate-800">{upcoming}</span> upcoming appointment{upcoming === 1 ? "" : "s"}
          </p>
        </div>
        <button data-testid="calendar-today" onClick={() => select(today)} className="dm-btn-ghost">Today</button>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="dm-panel overflow-hidden" data-testid="calendar-month">
          <div className="dm-panel-head">
            <button data-testid="calendar-prev" onClick={() => shiftMonth(-1)} aria-label="Previous month" className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"><ChevronLeft size={18} /></button>
            <h2 className="font-head text-lg font-semibold text-slate-800" data-testid="calendar-month-title">{monthTitle}</h2>
            <button data-testid="calendar-next" onClick={() => shiftMonth(1)} aria-label="Next month" className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"><ChevronRight size={18} /></button>
          </div>
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50/60">
            {WEEKDAYS.map((w) => <div key={w} className="py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500">{w}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d) => {
              const a = appts[d] || [];
              const v = visits[d] || [];
              const inMonth = d.slice(0, 7) === month;
              const isSel = d === selected;
              const isToday = d === today;
              return (
                <button key={d} data-testid={`calendar-day-${d}`} onClick={() => select(d)}
                  aria-label={`${parse(d).toDateString()}: ${a.length} appointments, ${v.length} visits`}
                  className={`relative flex min-h-[64px] flex-col items-stretch gap-1 border-b border-r border-slate-100 p-1.5 text-left transition-colors md:min-h-[96px] md:p-2 ${
                    isSel ? "bg-[var(--dm-accent-soft)]" : "hover:bg-slate-50"} ${inMonth ? "" : "opacity-40"}`}>
                  <span className={`grid h-6 w-6 place-items-center rounded-full font-mono text-xs ${
                    isToday ? "dm-accent-bg font-bold text-white" : isSel ? "dm-accent-text font-bold" : "text-slate-700"}`}>
                    {Number(d.slice(8))}
                  </span>
                  {/* Phones: compact counts. Wider screens: first names. */}
                  <span className="flex flex-wrap gap-1 md:hidden">
                    {a.length > 0 && <span className="rounded-full px-1.5 font-mono text-[10px] font-semibold text-white dm-accent-bg">{a.length}</span>}
                    {v.length > 0 && <span className="rounded-full bg-slate-200 px-1.5 font-mono text-[10px] font-semibold text-slate-600">{v.length}</span>}
                  </span>
                  <span className="hidden min-w-0 flex-col gap-0.5 md:flex">
                    {a.slice(0, 2).map((p) => (
                      <span key={p.id} className="truncate rounded px-1 py-0.5 text-[11px] font-semibold dm-accent-text" style={{ backgroundColor: "var(--dm-accent-soft)" }}>
                        {p.next_appointment_time ? `${p.next_appointment_time} ` : ""}{p.name}
                      </span>
                    ))}
                    {a.length > 2 && <span className="px-1 text-[11px] font-semibold text-slate-500">+{a.length - 2} more</span>}
                    {v.length > 0 && <span className="px-1 text-[11px] text-slate-500">{v.length} visit{v.length === 1 ? "" : "s"}</span>}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-4 px-4 py-3 text-xs text-slate-500 md:hidden">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full dm-accent-bg" /> Appointments</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-slate-300" /> Visits logged</span>
          </div>
        </div>

        <DayPanel key={selected} day={selected} today={today} appts={appts[selected] || []} visits={visits[selected] || []}
          patients={data.patients} consultants={data.consultants} save={save} />
      </div>
    </div>
  );
}

function DayPanel({ day, today, appts, visits, patients, consultants, save }) {
  const title = parse(day).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const isPast = day < today;
  const nameOf = (id) => patients.find((p) => p.id === id)?.name || "Unknown patient";
  const consultantOf = (id) => consultants.find((c) => c.id === id)?.name;

  return (
    <div className="space-y-4" data-testid="calendar-day-panel">
      <div className="dm-panel p-5">
        <p className="label-xs">{day === today ? "Today" : isPast ? "Past day" : "Upcoming"}</p>
        <h2 className="mt-1 font-head text-xl font-bold text-slate-900" data-testid="calendar-day-title">{title}</h2>
      </div>

      <div className="dm-panel overflow-hidden">
        <div className="dm-panel-head">
          <h3 className="flex items-center gap-2 font-head text-base font-semibold text-slate-800"><CalendarClock size={16} /> Appointments</h3>
          <span className="font-mono text-xs text-slate-500">{appts.length}</span>
        </div>
        {appts.length === 0 ? (
          <div className="dm-empty m-4">No appointments.</div>
        ) : (
          <ul className="max-h-[50vh] divide-y divide-slate-100 overflow-y-auto" data-testid="calendar-appointments">
            {appts.map((p) => <AppointmentRow key={p.id} p={p} today={today} save={save} />)}
          </ul>
        )}
        {!isPast && <BookForm day={day} patients={patients} save={save} />}
      </div>

      <div className="dm-panel overflow-hidden">
        <div className="dm-panel-head">
          <h3 className="flex items-center gap-2 font-head text-base font-semibold text-slate-800"><Stethoscope size={16} /> Visits logged</h3>
          <span className="font-mono text-xs text-slate-500">{visits.length}</span>
        </div>
        {visits.length === 0 ? (
          <div className="dm-empty m-4">No visits logged.</div>
        ) : (
          <ul className="max-h-[50vh] divide-y divide-slate-100 overflow-y-auto" data-testid="calendar-visits">
            {visits.map((v) => (
              <li key={v.id}>
                <Link to={`/app/patients/${v.patient_id}`} className="block px-5 py-3 transition-colors hover:bg-slate-50">
                  <p className="truncate text-sm font-semibold text-slate-800">{nameOf(v.patient_id)}</p>
                  <p className="truncate text-xs text-slate-500">
                    {[v.treatment, v.diagnosis, consultantOf(v.consultant_id)].filter(Boolean).join(" · ") || "—"}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AppointmentRow({ p, today, save }) {
  const [moving, setMoving] = useState(false);
  const [date, setDate] = useState((p.next_appointment || "").slice(0, 10));
  const [time, setTime] = useState(p.next_appointment_time || DEFAULT_TIME);

  const move = async () => {
    if (!date) return toast.error("Pick a date");
    await save("patients", { ...p, next_appointment: date, next_appointment_time: time || DEFAULT_TIME });
    toast.success(`${p.name} moved to ${parse(date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`);
    setMoving(false);
  };
  const cancel = async () => {
    await save("patients", { ...p, next_appointment: null, next_appointment_time: null });
    toast.success(`Appointment for ${p.name} cancelled`);
  };

  return (
    <li className="px-5 py-3" data-testid={`calendar-appt-${p.id}`}>
      <div className="flex items-center gap-3">
        <span className="w-16 shrink-0 font-mono text-xs font-semibold text-slate-800">{timeLabel(p.next_appointment_time)}</span>
        <Link to={`/app/patients/${p.id}`} className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-slate-800 hover:underline">{p.name}</span>
          <span className="block truncate text-xs text-slate-500">ID {p.code ?? "—"} · {p.phone || "No phone"}</span>
        </Link>
        {!moving && (
          <div className="flex shrink-0 items-center gap-1">
            {p.phone && (
              <a href={`tel:${p.phone.replace(/[^\d+]/g, "")}`} data-testid={`calendar-call-${p.id}`} aria-label={`Call ${p.name}`}
                className="rounded-full p-1.5 text-emerald-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700"><Phone size={15} /></a>
            )}
            <button data-testid={`calendar-move-${p.id}`} onClick={() => setMoving(true)} className="dm-btn-quiet !px-2.5 !py-1 !text-xs">Move</button>
            <button data-testid={`calendar-cancel-${p.id}`} onClick={cancel} aria-label={`Cancel appointment for ${p.name}`}
              className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"><X size={15} /></button>
          </div>
        )}
      </div>
      {moving && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input type="date" data-testid={`calendar-move-date-${p.id}`} className="dm-input !w-auto flex-1" min={today} value={date} onChange={(e) => setDate(e.target.value)} />
          <input type="time" data-testid={`calendar-move-time-${p.id}`} className="dm-input !w-36" value={time} onChange={(e) => setTime(e.target.value)} />
          <button data-testid={`calendar-move-save-${p.id}`} onClick={move} className="dm-btn-primary !px-3 !py-2"><Check size={14} /> Save</button>
          <button onClick={() => setMoving(false)} className="dm-btn-quiet !px-3 !py-2">Cancel</button>
        </div>
      )}
    </li>
  );
}

function BookForm({ day, patients, save }) {
  const [q, setQ] = useState("");
  const [patient, setPatient] = useState(null);
  const [time, setTime] = useState(DEFAULT_TIME);
  const t = q.trim().toLowerCase();
  const matches = useMemo(() => (t && !patient
    ? patients.filter((p) => (p.name || "").toLowerCase().includes(t) || (p.phone || "").includes(t) || String(p.code || "").includes(t)).slice(0, 6)
    : []), [t, patient, patients]);

  // A patient holds a single next appointment, so booking replaces any existing one.
  const existing = patient?.next_appointment && patient.next_appointment.slice(0, 10) !== day ? patient.next_appointment.slice(0, 10) : null;

  const book = async (e) => {
    e.preventDefault();
    if (!patient) return toast.error("Choose a patient");
    await save("patients", { ...patient, next_appointment: day, next_appointment_time: time || DEFAULT_TIME });
    toast.success(`Booked ${patient.name} at ${timeLabel(time || DEFAULT_TIME)}`);
    setPatient(null); setQ(""); setTime(DEFAULT_TIME);
  };

  return (
    <form onSubmit={book} className="space-y-3 border-t border-slate-200 bg-slate-50/60 p-4" data-testid="calendar-book-form">
      <p className="label-xs flex items-center gap-1.5"><CalendarPlus size={13} /> Book appointment</p>
      {patient ? (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-slate-800">{patient.name}</span>
            <span className="block truncate text-xs text-slate-500">ID {patient.code ?? "—"} · {patient.phone || "No phone"}</span>
          </span>
          <button type="button" onClick={() => { setPatient(null); setQ(""); }} aria-label="Change patient" className="rounded-full p-1 text-slate-400 hover:text-slate-700"><X size={15} /></button>
        </div>
      ) : (
        <div className="relative">
          <input data-testid="calendar-book-search" className="dm-input" placeholder="Search by name, phone, or Patient ID…" value={q} onChange={(e) => setQ(e.target.value)} />
          {matches.length > 0 && (
            <ul className="absolute left-0 right-0 top-full z-10 mt-1 max-h-60 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
              {matches.map((p) => (
                <li key={p.id}>
                  <button type="button" data-testid={`calendar-book-pick-${p.id}`} onClick={() => { setPatient(p); setTime(p.next_appointment_time || DEFAULT_TIME); }}
                    className="block w-full px-3 py-2 text-left hover:bg-slate-50">
                    <span className="block truncate text-sm font-semibold text-slate-800">{p.name}</span>
                    <span className="block truncate text-xs text-slate-500">ID {p.code ?? "—"} · {p.phone || "No phone"}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {t && !matches.length && <p className="mt-1.5 text-xs text-slate-500">No patients found. Add them from <Link to="/app/directory" className="dm-link">Directory</Link> first.</p>}
        </div>
      )}
      {existing && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="calendar-book-replaces">
          This replaces their appointment on {parse(existing).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}.
        </p>
      )}
      <div className="flex gap-2">
        <input type="time" data-testid="calendar-book-time" className="dm-input !w-36 shrink-0" value={time} onChange={(e) => setTime(e.target.value)} />
        <button type="submit" data-testid="calendar-book-submit" disabled={!patient} className="dm-btn-primary flex-1 disabled:opacity-40"><Check size={15} /> Book</button>
      </div>
    </form>
  );
}
